/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx, jsx } from '@atlaskit/css';
import DeleteIcon from '@atlaskit/icon/core/delete';
import { ExitingPersistence, Motion } from '@atlaskit/motion';
import { useResizing } from '@atlaskit/motion/resizing';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { EditDeleteConfirm } from './EditDeleteConfirm';
import { messages } from './ManageMemoryEditsSubscreen.messages';
import type { MemoryEditItem } from './types';

const motionStyles = cssMap({
	entering: {
		animationName: token('motion.keyframe.fade.in'),
		animationDuration: token('motion.duration.xlong'),
		animationTimingFunction: token('motion.easing.out.practical'),
	},
	exiting: {
		animationName: token('motion.keyframe.fade.out'),
		animationDuration: token('motion.duration.medium'),
		animationTimingFunction: token('motion.easing.in.practical'),
	},
});

const styles = cssMap({
	rowWrapper: {
		borderTop: `${token('border.width')} solid ${token('color.border')}`,
		boxSizing: 'border-box',
		position: 'relative',
	},
	rowWrapperFirst: {
		borderTop: 'none',
		boxSizing: 'border-box',
		position: 'relative',
		borderTopLeftRadius: token('radius.xlarge'),
		borderTopRightRadius: token('radius.xlarge'),
	},
	rowWrapperExpanding: {
		zIndex: 1,
		backgroundColor: token('elevation.surface'),
	},
	row: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: token('space.150'),
		width: '100%',
		paddingBlock: token('space.100'),
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.150'),
		backgroundColor: token('elevation.surface'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
	rowContent: {
		display: 'flex',
		paddingBlock: token('space.150'),
		alignItems: 'flex-start',
		gap: token('space.150'),
		alignSelf: 'stretch',
		flex: '1 1 0%',
		minWidth: '0px',
	},
	rowContentText: {
		overflowWrap: 'break-word',
		minWidth: '0px',
	},
	rowActions: {
		flexShrink: 0,
	},
});

type EditRowProps = {
	edit: MemoryEditItem;
	isFirst: boolean;
	confirmingId: string | null;
	isDeletingId: string | null;
	onDeleteClick: () => void;
	onCancelConfirm: () => void;
	onConfirmRemove: () => void;
};

export const EditRow = ({
	edit,
	isFirst,
	confirmingId,
	isDeletingId,
	onDeleteClick,
	onCancelConfirm,
	onConfirmRemove,
}: EditRowProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const isConfirming = confirmingId === edit.id;
	const isDeleting = isDeletingId === edit.id;
	const resizingProps = useResizing({
		dimension: 'height',
		duration: token('motion.duration.medium'),
		easing: token('motion.easing.out.practical'),
	});

	return (
		<Box
			xcss={cx(
				isFirst ? styles.rowWrapperFirst : styles.rowWrapper,
				isConfirming && styles.rowWrapperExpanding,
			)}
		>
			<div {...resizingProps}>
				<ExitingPersistence appear>
					{isConfirming ? (
						<Motion
							enteringAnimationXcss={motionStyles.entering}
							exitingAnimationXcss={motionStyles.exiting}
						>
							<EditDeleteConfirm
								edit={edit}
								onCancel={onCancelConfirm}
								onRemove={onConfirmRemove}
								isFirst={isFirst}
								isLoading={isDeleting}
							/>
						</Motion>
					) : (
						<Box xcss={styles.row} as="div">
							<Box xcss={styles.rowContent}>
								<Box xcss={styles.rowContentText}>
									<Text>{edit.content}</Text>
								</Box>
							</Box>
							<Box xcss={styles.rowActions}>
								<Tooltip content={formatMessage(messages.deleteButton)}>
									{(tooltipProps) => (
										<IconButton
											{...tooltipProps}
											appearance="subtle"
											icon={DeleteIcon}
											label={formatMessage(messages.deleteButton)}
											onClick={(e) => {
												e.stopPropagation();
												onDeleteClick();
											}}
										/>
									)}
								</Tooltip>
							</Box>
						</Box>
					)}
				</ExitingPersistence>
			</div>
		</Box>
	);
};
